import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminEventForm.scss';

const DEFAULT_FORM = {
  title: '',
  type: '',
  status: '',
  datetime: '',
  ends_at: '',
  venue: '',
  city: '',
  image_url: '',
  capacity: '',
  has_slots: false,
  is_member_only: false
};

const toLocalDateTimeInput = (
  value
) => {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  const offset =
    date.getTimezoneOffset();

  const localDate =
    new Date(
      date.getTime() -
        offset * 60000
    );

  return localDate
    .toISOString()
    .slice(0, 16);
};

export default function AdminEventForm() {
  const navigate =
    useNavigate();

  const {
    id
  } = useParams();

  const isEditing =
    Boolean(id);

  const apiBaseUrl = useMemo(() => {
    const envUrl =
      process.env.REACT_APP_API_URL ||
      '';

    return envUrl.endsWith('/')
      ? envUrl.slice(0, -1)
      : envUrl;
  }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    form,
    setForm
  ] = useState(DEFAULT_FORM);

  const [
    eventTypes,
    setEventTypes
  ] = useState([]);

  const [
    eventStatuses,
    setEventStatuses
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    selectedImage,
    setSelectedImage
  ] = useState(null);

  const [
    imagePreview,
    setImagePreview
  ] = useState('');

  const [
    uploadingImage,
    setUploadingImage
  ] = useState(false);

  useEffect(() => {
    const initialize =
      async () => {
        setLoading(true);
        setError('');

        try {
          /*
          |--------------------------------------------------------------------------
          | Load Event Enum Metadata
          |--------------------------------------------------------------------------
          */

          const metaResponse =
            await fetch(
              `${apiBaseUrl}/admin/events/meta`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const meta =
            await metaResponse.json();

          if (!metaResponse.ok) {
            throw new Error(
              meta?.message ||
                'Unable to load event options.'
            );
          }

          const types =
            meta.eventTypes || [];

          const statuses =
            meta.eventStatuses ||
            [];

          setEventTypes(
            types
          );

          setEventStatuses(
            statuses
          );

          /*
          |--------------------------------------------------------------------------
          | Edit Existing Event
          |--------------------------------------------------------------------------
          */

          if (isEditing) {
            const response =
              await fetch(
                `${apiBaseUrl}/admin/events/${id}`,
                {
                  headers: {
                    Authorization:
                      `Bearer ${token}`
                  }
                }
              );

            const data =
              await response.json();

            if (!response.ok) {
              throw new Error(
                data?.message ||
                  'Unable to load event.'
              );
            }

            setForm({
              ...DEFAULT_FORM,
              ...data.event,

              datetime:
                toLocalDateTimeInput(
                  data.event.datetime
                ),

              ends_at:
                toLocalDateTimeInput(
                  data.event.ends_at
                ),

              capacity:
                data.event.capacity ??
                ''
            });

            return;
          }

          /*
          |--------------------------------------------------------------------------
          | New Event Defaults
          |--------------------------------------------------------------------------
          */

          setForm(
            (previous) => ({
              ...previous,

              type:
                types[0] || '',

              status:
                statuses[0] ||
                ''
            })
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to initialize event form.'
          );
        } finally {
          setLoading(false);
        }
      };

    initialize();
  }, [
    apiBaseUrl,
    id,
    isEditing,
    token
  ]);

  /*
  |--------------------------------------------------------------------------
  | Normal Field Change
  |--------------------------------------------------------------------------
  */

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked
    } = event.target;

    setForm(
      (previous) => ({
        ...previous,

        [name]:
          type === 'checkbox'
            ? checked
            : value
      })
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Image Selection
  |--------------------------------------------------------------------------
  */

  const handleImageChange = (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp'
    ];

    if (
      !allowedTypes.includes(
        file.type
      )
    ) {
      setError(
        'Please select a JPG, PNG or WEBP image.'
      );

      return;
    }

    const maxSize =
      8 * 1024 * 1024;

    if (
      file.size > maxSize
    ) {
      setError(
        'Event image must be smaller than 8 MB.'
      );

      return;
    }

    setError('');

    setSelectedImage(
      file
    );

    if (imagePreview) {
      URL.revokeObjectURL(
        imagePreview
      );
    }

    const previewUrl =
      URL.createObjectURL(
        file
      );

    setImagePreview(
      previewUrl
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Upload Image
  |--------------------------------------------------------------------------
  */

  const uploadImage =
    async () => {
      if (!selectedImage) {
        return form.image_url ||
          null;
      }

      setUploadingImage(
        true
      );

      try {
        const imageData =
          new FormData();

        imageData.append(
          'image',
          selectedImage
        );

        const response =
          await fetch(
            `${apiBaseUrl}/admin/events/upload-image`,
            {
              method: 'POST',

              headers: {
                Authorization:
                  `Bearer ${token}`
              },

              body: imageData
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to upload event image.'
          );
        }

        return data.imageUrl;
      } finally {
        setUploadingImage(
          false
        );
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Existing Image Preview URL
  |--------------------------------------------------------------------------
  */

  const currentImageUrl =
    useMemo(() => {
      if (!form.image_url) {
        return '';
      }

      if (
        form.image_url.startsWith(
          'http://'
        ) ||
        form.image_url.startsWith(
          'https://'
        )
      ) {
        return form.image_url;
      }

      const path =
        form.image_url.startsWith(
          '/'
        )
          ? form.image_url
          : `/${form.image_url}`;

      return `${apiBaseUrl}${path}`;
    }, [
      apiBaseUrl,
      form.image_url
    ]);

  /*
  |--------------------------------------------------------------------------
  | Validation
  |--------------------------------------------------------------------------
  */

  const validate = () => {
    if (
      !form.title.trim()
    ) {
      return 'Event title is required.';
    }

    if (!form.type) {
      return 'Event type is required.';
    }

    if (!form.status) {
      return 'Event status is required.';
    }

    if (!form.datetime) {
      return 'Start date and time are required.';
    }

    if (
      form.ends_at &&
      new Date(
        form.ends_at
      ) <=
        new Date(
          form.datetime
        )
    ) {
      return 'Event end must be after its start time.';
    }

    if (
      form.capacity !== '' &&
      Number(
        form.capacity
      ) < 1
    ) {
      return 'Capacity must be at least 1.';
    }

    return '';
  };

  /*
  |--------------------------------------------------------------------------
  | Submit
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      const validationError =
        validate();

      if (validationError) {
        setError(
          validationError
        );

        return;
      }

      setSaving(true);
      setError('');

      try {
        /*
        |--------------------------------------------------------------------------
        | Upload Image First
        |--------------------------------------------------------------------------
        */

        const uploadedImageUrl =
          await uploadImage();

        /*
        |--------------------------------------------------------------------------
        | Build Event Payload
        |--------------------------------------------------------------------------
        */

        const payload = {
          ...form,

          image_url:
            uploadedImageUrl ||
            null,

          datetime:
            new Date(
              form.datetime
            ).toISOString(),

          ends_at:
            form.ends_at
              ? new Date(
                  form.ends_at
                ).toISOString()
              : null,

          capacity:
            form.capacity === ''
              ? null
              : Number(
                  form.capacity
                )
        };

        const url =
          isEditing
            ? `${apiBaseUrl}/admin/events/${id}`
            : `${apiBaseUrl}/admin/events`;

        const method =
          isEditing
            ? 'PATCH'
            : 'POST';

        /*
        |--------------------------------------------------------------------------
        | Save Event
        |--------------------------------------------------------------------------
        */

        const response =
          await fetch(
            url,
            {
              method,

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to save event.'
          );
        }

        navigate(
          `/admin/events/${data.event.event_id}`
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to save event.'
        );
      } finally {
        setSaving(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="admin-event-form-loading">
        Loading event...
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="admin-event-form-page">
      <section className="admin-event-form-heading">
        <div>
          <p className="admin-event-form-heading__eyebrow">
            Community Operations
          </p>

          <h2>
            {isEditing
              ? 'Edit Event'
              : 'Create Event'}
          </h2>

          <p>
            Build and manage a Tour
            Golfers community event.
          </p>
        </div>

        <button
          type="button"
          className="admin-event-form-heading__back"
          onClick={() =>
            navigate(
              isEditing
                ? `/admin/events/${id}`
                : '/admin/events'
            )
          }
        >
          ← Back
        </button>
      </section>

      <form
        className="admin-event-form"
        onSubmit={
          handleSubmit
        }
      >
        {error ? (
          <div className="admin-event-form__error">
            {error}
          </div>
        ) : null}

        {/* --------------------------------------------------
            Event
        -------------------------------------------------- */}

        <section className="admin-event-form-card">
          <div className="admin-event-form-card__header">
            <span>
              01
            </span>

            <div>
              <h3>
                Event
              </h3>

              <p>
                Core event information.
              </p>
            </div>
          </div>

          <div className="admin-event-form-grid">
            <div className="admin-event-form-field admin-event-form-field--full">
              <label>
                Event Name *
              </label>

              <input
                name="title"
                value={
                  form.title
                }
                onChange={
                  handleChange
                }
                placeholder="Tour Golfers Simulator Night"
              />
            </div>

            <div className="admin-event-form-field">
              <label>
                Type *
              </label>

              <select
                name="type"
                value={
                  form.type
                }
                onChange={
                  handleChange
                }
              >
                {eventTypes.map(
                  (type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type.replaceAll(
                        '_',
                        ' '
                      )}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="admin-event-form-field">
              <label>
                Status *
              </label>

              <select
                name="status"
                value={
                  form.status
                }
                onChange={
                  handleChange
                }
              >
                {eventStatuses.map(
                  (status) => (
                    <option
                      key={status}
                      value={status}
                    >
                      {status.replaceAll(
                        '_',
                        ' '
                      )}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>
        </section>

        {/* --------------------------------------------------
            Schedule
        -------------------------------------------------- */}

        <section className="admin-event-form-card">
          <div className="admin-event-form-card__header">
            <span>
              02
            </span>

            <div>
              <h3>
                Schedule
              </h3>

              <p>
                Event date and time.
              </p>
            </div>
          </div>

          <div className="admin-event-form-grid">
            <div className="admin-event-form-field">
              <label>
                Start *
              </label>

              <input
                type="datetime-local"
                name="datetime"
                value={
                  form.datetime
                }
                onChange={
                  handleChange
                }
              />
            </div>

            <div className="admin-event-form-field">
              <label>
                End
              </label>

              <input
                type="datetime-local"
                name="ends_at"
                value={
                  form.ends_at
                }
                onChange={
                  handleChange
                }
              />
            </div>
          </div>
        </section>

        {/* --------------------------------------------------
            Location + Image
        -------------------------------------------------- */}

        <section className="admin-event-form-card">
          <div className="admin-event-form-card__header">
            <span>
              03
            </span>

            <div>
              <h3>
                Location & Image
              </h3>

              <p>
                Event venue and
                member-facing image.
              </p>
            </div>
          </div>

          <div className="admin-event-form-grid">
            <div className="admin-event-form-field">
              <label>
                Venue
              </label>

              <input
                name="venue"
                value={
                  form.venue
                }
                onChange={
                  handleChange
                }
                placeholder="Venue name"
              />
            </div>

            <div className="admin-event-form-field">
              <label>
                City
              </label>

              <input
                name="city"
                value={
                  form.city
                }
                onChange={
                  handleChange
                }
                placeholder="Oakville"
              />
            </div>

            <div className="admin-event-form-field admin-event-form-field--full">
              <label>
                Event Image
              </label>

              <div className="admin-event-image-upload">
                <label className="admin-event-image-upload__button">
                  Choose Image

                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={
                      handleImageChange
                    }
                  />
                </label>

                {selectedImage ? (
                  <span className="admin-event-image-upload__filename">
                    {
                      selectedImage.name
                    }
                  </span>
                ) : null}
              </div>

              {imagePreview ? (
                <div className="admin-event-image-preview">
                  <img
                    src={
                      imagePreview
                    }
                    alt="Event preview"
                  />
                </div>
              ) : currentImageUrl ? (
                <div className="admin-event-image-preview">
                  <img
                    src={
                      currentImageUrl
                    }
                    alt="Current event"
                  />
                </div>
              ) : (
                <p className="admin-event-image-upload__help">
                  Upload a JPG, PNG or
                  WEBP image. Maximum
                  size 8 MB.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* --------------------------------------------------
            Registration
        -------------------------------------------------- */}

        <section className="admin-event-form-card">
          <div className="admin-event-form-card__header">
            <span>
              04
            </span>

            <div>
              <h3>
                Registration
              </h3>

              <p>
                Attendance settings.
              </p>
            </div>
          </div>

          <div className="admin-event-form-grid">
            <div className="admin-event-form-field">
              <label>
                Capacity
              </label>

              <input
                type="number"
                min="1"
                name="capacity"
                value={
                  form.capacity
                }
                onChange={
                  handleChange
                }
                placeholder="32"
              />
            </div>

            <div className="admin-event-form-checkboxes">
              <label className="admin-event-form-checkbox">
                <input
                  type="checkbox"
                  name="has_slots"
                  checked={
                    form.has_slots
                  }
                  onChange={
                    handleChange
                  }
                />

                <div>
                  <strong>
                    Slot Based Event
                  </strong>

                  <span>
                    Members book a
                    specific event slot.
                  </span>
                </div>
              </label>

              <label className="admin-event-form-checkbox">
                <input
                  type="checkbox"
                  name="is_member_only"
                  checked={
                    form.is_member_only
                  }
                  onChange={
                    handleChange
                  }
                />

                <div>
                  <strong>
                    Members Only
                  </strong>

                  <span>
                    Restrict registration
                    to members.
                  </span>
                </div>
              </label>
            </div>
          </div>
        </section>

        {/* --------------------------------------------------
            Actions
        -------------------------------------------------- */}

        <div className="admin-event-form-actions">
          <button
            type="button"
            className="admin-event-form-actions__secondary"
            onClick={() =>
              navigate(
                '/admin/events'
              )
            }
            disabled={
              saving ||
              uploadingImage
            }
          >
            Cancel
          </button>

          <button
            type="submit"
            className="admin-event-form-actions__primary"
            disabled={
              saving ||
              uploadingImage
            }
          >
            {uploadingImage
              ? 'Uploading Image...'
              : saving
              ? 'Saving...'
              : isEditing
              ? 'Save Event'
              : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
}